/**
 * Shared design tokens for the Client View tab.
 * Keep palette here so the map, chips, and cards stay in sync.
 */

import type { CallListBand, ClientViewDealer, ClientViewMapPoint, CoreClientsMeta, PriorityCounts } from '../../api/types';

// -----------------------------------------------------------------------------
// Priority (vin_action_filter) — ordered highest → lowest urgency
// -----------------------------------------------------------------------------

// The wire key stays `active_gt_1_day` though the rule is now >3 days: it is
// persisted in saved views, which silently drop keys they don't recognise.
// Likewise `auto_listed` / Snowflake 'AUTO-LISTED' are kept on the wire; the app
// calls the bucket "Auto-Launch" (Oct 2026, UI-only rename).
export type PriorityKey =
  | 'ready_to_list'
  | 'auto_listed'
  | 'expired'
  | 'active_gt_1_day'
  | 'potential_for_inspection'
  | 'other';

export const PRIORITY_ORDER: PriorityKey[] = [
  'ready_to_list',
  'auto_listed',
  'expired',
  'active_gt_1_day',
  'potential_for_inspection',
  'other',
];

export const PRIORITY_LABEL: Record<PriorityKey, string> = {
  ready_to_list: 'Ready to List',
  auto_listed: 'Auto-Launch',
  expired: 'Expired',
  active_gt_1_day: 'Active > 3 days',
  potential_for_inspection: 'Potential for Inspection',
  other: 'Other',
};

// Raw vin_action_filter values shown verbatim in VIN tables, except where the app
// renames a bucket without a Snowflake change.
const VIN_ACTION_DISPLAY: Record<string, string> = {
  'AUTO-LISTED': 'AUTO-LAUNCH',
};

export const vinActionDisplay = (raw: string | null | undefined): string | null | undefined =>
  raw ? (VIN_ACTION_DISPLAY[raw] ?? raw) : raw;

// Distinct from existing Main-tab palette (the SIFT stage colors). Chosen to be
// colorblind-friendlier and to read clearly on light + dark backgrounds.
export const PRIORITY_COLOR: Record<PriorityKey, { bg: string; fg: string; pin: string }> = {
  ready_to_list:            { bg: '#dcfce7', fg: '#15803d', pin: '#16a34a' }, // green
  auto_listed:              { bg: '#ede9fe', fg: '#6d28d9', pin: '#7c3aed' }, // violet
  expired:                  { bg: '#fee2e2', fg: '#b91c1c', pin: '#dc2626' }, // red
  active_gt_1_day:          { bg: '#dbeafe', fg: '#1d4ed8', pin: '#2563eb' }, // blue
  potential_for_inspection: { bg: '#fef3c7', fg: '#a16207', pin: '#d97706' }, // amber
  other:                    { bg: '#e5e7eb', fg: '#4b5563', pin: '#6b7280' }, // gray
};

// -----------------------------------------------------------------------------
// Tier — derived directly from CoreClientsMeta.client_status
// Canonical values: Core Client, At Risk, Emerging, Low MMI Activity,
// Non-MMI Activity, Inactive
// -----------------------------------------------------------------------------

export type TierKey =
  | 'core_client'
  | 'at_risk'
  | 'emerging'
  | 'low_mmi_activity'
  | 'non_mmi_activity'
  | 'inactive';

export const TIER_ORDER: TierKey[] = [
  'core_client',
  'at_risk',
  'emerging',
  'low_mmi_activity',
  'non_mmi_activity',
  'inactive',
];

export const TIER_LABEL: Record<TierKey, string> = {
  core_client: 'Core Client',
  at_risk: 'At Risk',
  emerging: 'Emerging',
  low_mmi_activity: 'Low MMI Activity',
  non_mmi_activity: 'Non-MMI Activity',
  inactive: 'Inactive',
};

// Inspection call list bands, most urgent first. Server sends a band_label per client row, but
// band_counts is keyed only, so the labels are mirrored here for count-only displays.
export const CALL_LIST_BAND_ORDER: CallListBand[] = [
  'new_client_no_repeat',
  'due_now',
  'coming_up',
  'cadence_slipped',
  'has_inventory_no_recent_inspection',
  'dormant',
];

export const CALL_LIST_BAND_LABEL: Record<CallListBand, string> = {
  new_client_no_repeat: 'New, no repeat',
  due_now: 'Due now',
  coming_up: 'Coming up',
  cadence_slipped: 'Cadence slipped',
  has_inventory_no_recent_inspection: 'Has inventory, no recent inspection',
  dormant: 'Dormant',
};

// Halo / tier colors — chosen to NOT collide with priority palette above.
export const TIER_COLOR: Record<TierKey, string> = {
  core_client: '#7c3aed',      // violet
  at_risk: '#ea580c',          // orange (warning)
  emerging: '#0891b2',         // cyan (growth)
  low_mmi_activity: '#a16207', // mustard
  non_mmi_activity: '#65a30d', // olive/lime
  inactive: '#94a3b8',         // slate
};

/**
 * Classify a dealer into a tier using the canonical client_status value.
 * Recognized values (case/whitespace-insensitive):
 *   "Core Client", "At Risk", "Emerging", "Low MMI Activity",
 *   "Non-MMI Activity", "Inactive"
 * Anything else (including null/undefined) falls back to "inactive" — by
 * upstream contract every dealer carries one of the recognized values,
 * so this branch should not be reached in practice.
 */
export function classifyTier(core: CoreClientsMeta | undefined | null): TierKey {
  if (!core) return 'inactive';
  const status = (core.client_status || '').trim().toLowerCase();
  switch (status) {
    case 'core client': return 'core_client';
    case 'at risk': return 'at_risk';
    case 'emerging': return 'emerging';
    case 'low mmi activity': return 'low_mmi_activity';
    case 'non-mmi activity': return 'non_mmi_activity';
    case 'inactive': return 'inactive';
    default: return 'inactive';
  }
}

/**
 * Sum of currently-selected priority bucket counts for a dealer.
 * If no priorities are selected, falls back to total of all buckets.
 */
export function priorityScore(dealer: ClientViewDealer, activePriorities: PriorityKey[]): number {
  if (!dealer.priority_counts) return 0;
  const keys = activePriorities.length > 0 ? activePriorities : PRIORITY_ORDER;
  return keys.reduce((sum, k) => sum + (dealer.priority_counts[k] || 0), 0);
}

/**
 * Lexicographic comparator: orders dealers by priority bucket counts.
 * - When `activePriorities` is non-empty: orders by the selected buckets in
 *   the order the user selected them (first chip wins ties, etc.).
 * - When empty: falls back to PRIORITY_ORDER, so dealers with the highest
 *   `ready_to_list` count come first, ties broken by `expired`, then
 *   `active_gt_1_day`, etc. Final tie-break on seller_name ASC.
 */
export function compareDealersByPriority(
  a: ClientViewDealer,
  b: ClientViewDealer,
  activePriorities: PriorityKey[],
): number {
  const keys = activePriorities.length > 0 ? activePriorities : PRIORITY_ORDER;
  for (const k of keys) {
    const av = a.priority_counts?.[k] || 0;
    const bv = b.priority_counts?.[k] || 0;
    if (av !== bv) return bv - av;
  }
  return (a.seller_name || '').localeCompare(b.seller_name || '');
}

// -----------------------------------------------------------------------------
// Thin map-point helpers (ClientViewMapPoint carries priority_counts as a 5-int
// array in PRIORITY_ORDER and tier as a TIER_ORDER index). These mirror the
// rich-dealer helpers above so the map + carousel list can run off the thin
// payload without ever materializing 140k rich dealer objects.
// -----------------------------------------------------------------------------

/** Position of each priority bucket inside ClientViewMapPoint.priority_counts. */
export const PRIORITY_INDEX: Record<PriorityKey, number> = {
  ready_to_list: 0,
  auto_listed: 1,
  expired: 2,
  active_gt_1_day: 3,
  potential_for_inspection: 4,
  other: 5,
};

/** Read a single bucket count from a thin point's priority_counts array. */
export function pointPriorityCount(point: ClientViewMapPoint, k: PriorityKey): number {
  return point.priority_counts?.[PRIORITY_INDEX[k]] || 0;
}

/** Dominant priority bucket for a thin point (first non-zero in PRIORITY_ORDER). */
export function pointDominantPriority(point: ClientViewMapPoint): PriorityKey {
  for (const k of PRIORITY_ORDER) {
    if (pointPriorityCount(point, k) > 0) return k;
  }
  return 'other';
}

/** Map a thin point's integer `tier` (TIER_ORDER index) to its TierKey. */
export function tierKeyFromIndex(tier: number): TierKey {
  return TIER_ORDER[tier] ?? 'inactive';
}

/** Convert a thin point's priority_counts array into the rich object form. */
export function priorityCountsToObject(arr: number[] | undefined): PriorityCounts {
  return {
    ready_to_list: arr?.[PRIORITY_INDEX.ready_to_list] || 0,
    auto_listed: arr?.[PRIORITY_INDEX.auto_listed] || 0,
    expired: arr?.[PRIORITY_INDEX.expired] || 0,
    active_gt_1_day: arr?.[PRIORITY_INDEX.active_gt_1_day] || 0,
    potential_for_inspection: arr?.[PRIORITY_INDEX.potential_for_inspection] || 0,
    other: arr?.[PRIORITY_INDEX.other] || 0,
  };
}

/** Lexicographic priority comparator for thin points (mirrors compareDealersByPriority). */
export function comparePointsByPriority(
  a: ClientViewMapPoint,
  b: ClientViewMapPoint,
  activePriorities: PriorityKey[],
): number {
  return (
    priorityLexCompare(a, b, activePriorities) ||
    (a.seller_name || '').localeCompare(b.seller_name || '')
  );
}

/** The bucket-count half of the priority order — 0 when every bucket ties. */
function priorityLexCompare(
  a: ClientViewMapPoint,
  b: ClientViewMapPoint,
  activePriorities: PriorityKey[],
): number {
  const keys = activePriorities.length > 0 ? activePriorities : PRIORITY_ORDER;
  for (const k of keys) {
    const av = pointPriorityCount(a, k);
    const bv = pointPriorityCount(b, k);
    if (av !== bv) return bv - av;
  }
  return 0;
}

// -----------------------------------------------------------------------------
// Card metrics carried on every thin point (`ClientViewMapPoint.metrics`) so the
// carousel can sort AND range-filter the WHOLE board on the card KPIs, not just
// the ~25 dealers whose card bodies the visible page happens to have fetched.
// The array is positional — this order is the contract with the backend
// (`SnowflakeService._MAP_POINT_METRIC_COLUMNS`). Append only, never reorder.
// -----------------------------------------------------------------------------

/**
 * The time grain every card KPI, sort and metric filter reads. One grain drives the
 * whole board: the card has room for one reading, and sorting on a grain the card
 * does not show is what left "Listed" filterable but reconcilable against nothing.
 *
 * The positional metrics array below is unchanged — only which windows fill it —
 * so persisted saved views keep resolving.
 */
export const METRIC_GRAINS = ['week', 'month', 'quarter', 'period', 'ytd'] as const;
export type MetricGrain = (typeof METRIC_GRAINS)[number];
export const DEFAULT_METRIC_GRAIN: MetricGrain = 'period';

export const METRIC_GRAIN_LABEL: Record<MetricGrain, string> = {
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  period: 'Period (3mo)',
  ytd: 'YTD',
};

/** How each grain's "vs prior" comparison reads, for card tooltips. */
export const METRIC_GRAIN_PRIOR_LABEL: Record<MetricGrain, string> = {
  week: 'the last full week',
  month: 'the last full month',
  quarter: 'the last full quarter',
  period: 'the prior 3 months',
  ytd: 'the same span last year',
};

export const MAP_POINT_METRIC_ORDER = [
  'inspections_current',
  'inspections_prior',
  'mmi_conversion_current',
  'mmi_conversion_prior',
  'insp_to_core',
  // Net SALES needed for Core since the designation moved to the in-period test.
  // Key kept as-is so persisted saved views keep resolving.
  'sold_to_core',
  'listed_current',
  'listed_prior',
  'days_since_contact',
  // Append-only: the array is positional and persisted saved views resolve by index.
  'days_since_inspection',
] as const;

export type MapPointMetric = (typeof MAP_POINT_METRIC_ORDER)[number];

const METRIC_INDEX: Record<MapPointMetric, number> = MAP_POINT_METRIC_ORDER.reduce(
  (acc, k, i) => { acc[k] = i; return acc; },
  {} as Record<MapPointMetric, number>,
);

/** Read one card metric off a thin point. `null` = the dealer has no value. */
export function pointMetric(point: ClientViewMapPoint, k: MapPointMetric): number | null {
  const v = point.metrics?.[METRIC_INDEX[k]];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Period-over-period change as a signed ratio (0.25 = +25%), matching the card's
 * trend badge. `null` when either side is missing, so trend sorts park dealers
 * with no comparison at the end rather than pretending they were flat.
 */
function trend(curr: number | null, prior: number | null): number | null {
  if (curr === null || prior === null) return null;
  if (prior === 0) return curr === 0 ? 0 : 1;
  return (curr - prior) / Math.abs(prior);
}

// -----------------------------------------------------------------------------
// Carousel sort
// -----------------------------------------------------------------------------

export type SortKey =
  | 'priority'
  | 'priority_total'
  | PriorityKey
  | 'tier'
  | 'inspections'
  | 'inspections_trend'
  | 'mmi_conversion'
  | 'mmi_conversion_trend'
  | 'insp_to_core'
  | 'sold_to_core'
  | 'listed'
  | 'listed_trend'
  | 'days_since_contact'
  | 'days_since_inspection'
  | 'distance'
  | 'seller_name';

export type SortDir = 'asc' | 'desc';

/** One field in the carousel's sort chain, with its own direction. */
export interface SortEntry {
  key: SortKey;
  dir: SortDir;
}

/** Ready-to-list-first priority order — the carousel's out-of-the-box sort. */
export const DEFAULT_SORTS: SortEntry[] = [{ key: 'priority', dir: 'desc' }];

export interface SortOption {
  key: SortKey;
  label: string;
  group: string;
  /** The direction shown first when the option is picked (its "most useful" end). */
  defaultDir: SortDir;
}

export const SORT_OPTIONS: SortOption[] = [
  { key: 'priority', label: 'Priority (default)', group: 'Priority', defaultDir: 'desc' },
  { key: 'priority_total', label: 'Total priority VINs', group: 'Priority', defaultDir: 'desc' },
  ...PRIORITY_ORDER.map<SortOption>((k) => ({
    key: k, label: PRIORITY_LABEL[k], group: 'Priority', defaultDir: 'desc',
  })),
  { key: 'tier', label: 'Tier', group: 'Tier', defaultDir: 'asc' },
  { key: 'inspections', label: 'Inspections', group: 'Metrics', defaultDir: 'desc' },
  { key: 'inspections_trend', label: 'Inspections trend', group: 'Metrics', defaultDir: 'desc' },
  { key: 'mmi_conversion', label: 'MMI conversion', group: 'Metrics', defaultDir: 'desc' },
  { key: 'mmi_conversion_trend', label: 'MMI conversion trend', group: 'Metrics', defaultDir: 'desc' },
  { key: 'insp_to_core', label: 'Inspections to Core', group: 'Metrics', defaultDir: 'asc' },
  { key: 'sold_to_core', label: 'Net sales to Core', group: 'Metrics', defaultDir: 'asc' },
  { key: 'listed', label: 'Listed', group: 'Metrics', defaultDir: 'desc' },
  { key: 'listed_trend', label: 'Listed trend', group: 'Metrics', defaultDir: 'desc' },
  { key: 'days_since_contact', label: 'Days since last contact', group: 'Metrics', defaultDir: 'desc' },
  // Ascending by default: a FRESH inspection is the actionable end (the units are
  // still on the lot and the CR is current), unlike contact recency.
  { key: 'days_since_inspection', label: 'Days since last inspection', group: 'Metrics', defaultDir: 'asc' },
  { key: 'distance', label: 'Distance', group: 'Dealer', defaultDir: 'asc' },
  { key: 'seller_name', label: 'Dealer name', group: 'Dealer', defaultDir: 'asc' },
];

export const SORT_LABEL: Record<SortKey, string> = SORT_OPTIONS.reduce(
  (acc, o) => { acc[o.key] = o.label; return acc; },
  {} as Record<SortKey, string>,
);

export const DEFAULT_SORT_DIR: Record<SortKey, SortDir> = SORT_OPTIONS.reduce(
  (acc, o) => { acc[o.key] = o.defaultDir; return acc; },
  {} as Record<SortKey, SortDir>,
);

export function isSortKey(value: unknown): value is SortKey {
  return typeof value === 'string' && value in SORT_LABEL;
}

export function isDefaultSorts(sorts: SortEntry[]): boolean {
  return sorts.length === DEFAULT_SORTS.length
    && sorts.every((s, i) => s.key === DEFAULT_SORTS[i].key && s.dir === DEFAULT_SORTS[i].dir);
}

/**
 * The single sortable number behind a sort key, or `null` when the dealer has no
 * value for it. `'priority'` and `'seller_name'` have no numeric value — they are
 * handled directly by `comparePointsBySort`.
 */
function sortValue(point: ClientViewMapPoint, key: SortKey): number | null {
  switch (key) {
    case 'priority_total':
      return PRIORITY_ORDER.reduce((sum, k) => sum + pointPriorityCount(point, k), 0);
    case 'tier':
      return point.tier;
    case 'inspections':
      return pointMetric(point, 'inspections_current');
    case 'inspections_trend':
      return trend(pointMetric(point, 'inspections_current'), pointMetric(point, 'inspections_prior'));
    case 'mmi_conversion':
      return pointMetric(point, 'mmi_conversion_current');
    case 'mmi_conversion_trend':
      return trend(pointMetric(point, 'mmi_conversion_current'), pointMetric(point, 'mmi_conversion_prior'));
    case 'insp_to_core':
      return pointMetric(point, 'insp_to_core');
    case 'sold_to_core':
      return pointMetric(point, 'sold_to_core');
    case 'listed':
      return pointMetric(point, 'listed_current');
    case 'listed_trend':
      return trend(pointMetric(point, 'listed_current'), pointMetric(point, 'listed_prior'));
    case 'days_since_contact':
      return pointMetric(point, 'days_since_contact');
    case 'days_since_inspection':
      return pointMetric(point, 'days_since_inspection');
    case 'distance':
      return point.distance_miles ?? null;
    default:
      return PRIORITY_ORDER.includes(key as PriorityKey)
        ? pointPriorityCount(point, key as PriorityKey)
        : null;
  }
}

/**
 * Comparator for ONE sort field — 0 when the two dealers tie on it, so the next
 * field in the chain can break the tie. Dealers with no value for the field
 * always sink to the bottom (in BOTH directions), so flipping a field to
 * ascending doesn't fill the first page with blanks.
 */
function compareByField(
  a: ClientViewMapPoint,
  b: ClientViewMapPoint,
  { key, dir }: SortEntry,
  activePriorities: PriorityKey[],
): number {
  if (key === 'priority') {
    const cmp = priorityLexCompare(a, b, activePriorities);
    return dir === 'desc' ? cmp : -cmp;
  }
  if (key === 'seller_name') {
    const byName = (a.seller_name || '').localeCompare(b.seller_name || '');
    return dir === 'asc' ? byName : -byName;
  }
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  if (av === bv) return 0;
  return dir === 'desc' ? bv - av : av - bv;
}

/**
 * Chain the carousel's sort fields: the first field that separates two dealers
 * wins, each field carrying its OWN direction. Ties on every field break on
 * dealer name.
 */
export function comparePointsBySorts(
  a: ClientViewMapPoint,
  b: ClientViewMapPoint,
  sorts: SortEntry[],
  activePriorities: PriorityKey[],
): number {
  for (const entry of sorts) {
    const cmp = compareByField(a, b, entry, activePriorities);
    if (cmp !== 0) return cmp;
  }
  return (a.seller_name || '').localeCompare(b.seller_name || '');
}

// -----------------------------------------------------------------------------
// Numeric metric range filters (min/max per metric, applied to the whole board)
// -----------------------------------------------------------------------------

export type MetricFilterKey =
  | 'priority_total'
  | PriorityKey
  | 'inspections'
  | 'inspections_trend'
  | 'mmi_conversion'
  | 'mmi_conversion_trend'
  | 'insp_to_core'
  | 'sold_to_core'
  | 'listed'
  | 'listed_trend'
  | 'days_since_contact'
  | 'days_since_inspection'
  | 'distance';

/** `null` on either bound means "unbounded on that side". */
export interface MetricRange {
  min: number | null;
  max: number | null;
}

export type MetricFilters = Partial<Record<MetricFilterKey, MetricRange>>;

/** How a metric's range inputs are entered/displayed. */
export type MetricUnit = 'count' | 'percent' | 'miles' | 'days';

export interface MetricFilterOption {
  key: MetricFilterKey;
  label: string;
  group: string;
  unit: MetricUnit;
}

export const METRIC_FILTER_OPTIONS: MetricFilterOption[] = [
  { key: 'priority_total', label: 'Total priority VINs', group: 'Priority', unit: 'count' },
  ...PRIORITY_ORDER.map<MetricFilterOption>((k) => ({
    key: k, label: PRIORITY_LABEL[k], group: 'Priority', unit: 'count',
  })),
  { key: 'inspections', label: 'Inspections', group: 'Metrics', unit: 'count' },
  { key: 'inspections_trend', label: 'Inspections trend', group: 'Metrics', unit: 'percent' },
  { key: 'mmi_conversion', label: 'MMI conversion', group: 'Metrics', unit: 'percent' },
  { key: 'mmi_conversion_trend', label: 'MMI conversion trend', group: 'Metrics', unit: 'percent' },
  { key: 'insp_to_core', label: 'Inspections to Core', group: 'Metrics', unit: 'count' },
  { key: 'sold_to_core', label: 'Net sales to Core', group: 'Metrics', unit: 'count' },
  { key: 'listed', label: 'Listed', group: 'Metrics', unit: 'count' },
  { key: 'listed_trend', label: 'Listed trend', group: 'Metrics', unit: 'percent' },
  { key: 'days_since_contact', label: 'Days since last contact', group: 'Metrics', unit: 'days' },
  { key: 'days_since_inspection', label: 'Days since last inspection', group: 'Metrics', unit: 'days' },
  { key: 'distance', label: 'Distance', group: 'Dealer', unit: 'miles' },
];

/** Percent metrics are stored as ratios but entered as whole percents. */
export function metricScale(unit: MetricUnit): number {
  return unit === 'percent' ? 100 : 1;
}

export function countActiveMetricFilters(filters: MetricFilters): number {
  return Object.values(filters).filter(
    (r) => r && (r.min !== null || r.max !== null),
  ).length;
}

/**
 * Whether a dealer satisfies every active range. A dealer with no value for a
 * bounded metric is EXCLUDED — "MMI conversion >= 20%" should not keep dealers
 * whose conversion is unknown.
 */
export function matchesMetricFilters(point: ClientViewMapPoint, filters: MetricFilters): boolean {
  for (const [k, range] of Object.entries(filters) as [MetricFilterKey, MetricRange | undefined][]) {
    if (!range || (range.min === null && range.max === null)) continue;
    const v = sortValue(point, k as SortKey);
    if (v === null) return false;
    if (range.min !== null && v < range.min) return false;
    if (range.max !== null && v > range.max) return false;
  }
  return true;
}

